import React, { useCallback, useEffect, useState } from 'react';

import { api } from '../../lib/api';
import { soldesChauffeurs } from '../../lib/argentApi';
import { messageErreur } from '../../lib/erreurs';
import { formatDate, formatMontant } from '../../lib/formats';
import { nomLisible } from '../../lib/libelles';
import { useSyncOuEnLigne } from '../../context/SyncContext';
import FinancialStatement, { financialKey } from '../FinancialStatement';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { LoadingState } from '../ui/loading-state';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet';
import { toast } from '../ui/sonner';
import DetailEcriture from './DetailEcriture';
import ExpenseForm from './ExpenseForm';
import { FenetreGeste } from './NouvelleOperation';
import TableauSoldes from './TableauSoldes';

/**
 * Onglet « Chauffeurs » de l'écran Argent (revue de l'écran Argent, lot 6).
 *
 * Combien d'argent est chez les chauffeurs: une ligne par chauffeur, en une
 * seule lecture du serveur (`/finance/drivers/balances`) — avances données,
 * frais justifiés, reliquats rendus, reste chez lui. Par défaut, ceux qui ont
 * encore de l'argent à justifier; la case montre tout le monde. Un clic ouvre
 * le panneau du chauffeur: relevé, avance, reliquat, frais, reprise des
 * avances historiques.
 */

const pluriel = (n, mot) => `${n} ${mot}${n > 1 ? 's' : ''}`;

function ReprisesChauffeur({ chauffeurId, peutSaisir, onModifie }) {
  const enLigne = useSyncOuEnLigne().isOnline !== false;
  const [voyages, setVoyages] = useState([]);
  const [envoi, setEnvoi] = useState('');
  const charger = useCallback(async () => {
    try {
      const reponse = await api.get(`/finance/drivers/${encodeURIComponent(chauffeurId)}/history-review`);
      // Seuls les voyages avec une avance ancienne pas encore au journal.
      setVoyages((reponse?.data?.data || []).filter((v) => !v.adopted && !v.in_journal && Number(v.advance_amount) > 0));
    } catch (e) {
      setVoyages([]);
    }
  }, [chauffeurId]);
  useEffect(() => {
    charger();
  }, [charger]);
  if (!voyages.length) return null;
  const reprendre = async (tripId) => {
    setEnvoi(tripId);
    try {
      await api.post(`/finance/trips/${tripId}/adopt-history`, { idempotency_key: financialKey() });
      toast.success('Écritures du voyage reprises.');
      await charger();
      onModifie?.();
    } catch (e) {
      toast.error(messageErreur(e, 'La reprise a échoué.'));
    } finally {
      setEnvoi('');
    }
  };
  return (
    <section className="mt-5 space-y-2" aria-label="Avances historiques à reprendre">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Avances historiques à reprendre ({voyages.length})</h3>
      <ul className="divide-y rounded-lg border text-sm">
        {voyages.map((v) => (
          <li key={v.trip_id} className="flex items-center justify-between gap-2 px-3 py-1.5">
            <span>{[v.tractor_plate, v.created_at ? formatDate(v.created_at) : null, `avance ${formatMontant(v.advance_amount)}`].filter(Boolean).join(' · ')}</span>
            {peutSaisir && (
              <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={!enLigne || envoi === v.trip_id} onClick={() => reprendre(v.trip_id)}>Reprendre</Button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function PanneauChauffeur({ chauffeurId, ligne, peutSaisir, onFermer, onGeste, onFrais, rafraichir, onModifie }) {
  const [ecriture, setEcriture] = useState(null);
  const devise = 'XOF';
  return (
    <Sheet open={Boolean(chauffeurId)} onOpenChange={(ouvert) => !ouvert && onFermer()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-4xl" data-testid="panneau-chauffeur">
        <SheetHeader>
          <SheetTitle>{nomLisible(ligne?.name, 'Chauffeur')}</SheetTitle>
          <SheetDescription>
            {ligne
              ? [ligne.truck_plate, `reste chez lui ${formatMontant(ligne.balance, devise)}`, ligne.last_advance_at ? `dernière avance le ${formatDate(ligne.last_advance_at)}` : null].filter(Boolean).join(' · ')
              : 'Argent confié au chauffeur'}
          </SheetDescription>
        </SheetHeader>
        {peutSaisir && (
          <div className="mt-3 flex flex-wrap gap-2">
            <Button type="button" size="sm" onClick={() => onGeste('avance')}>Donner une avance</Button>
            <Button type="button" size="sm" variant="outline" onClick={() => onGeste('rendu')}>Reliquat rendu</Button>
            <Button type="button" size="sm" variant="outline" onClick={onFrais}>Frais justifiés</Button>
          </div>
        )}
        <ReprisesChauffeur chauffeurId={chauffeurId} peutSaisir={peutSaisir} onModifie={onModifie} />
        <div className="mt-5">
          <FinancialStatement partyType="DRIVER" partyId={chauffeurId} refresh={rafraichir} onSelect={(operation) => setEcriture(operation.id)} />
        </div>
        <DetailEcriture operationId={ecriture} onFermer={() => setEcriture(null)} onModifie={onModifie} peutSaisir={peutSaisir} />
      </SheetContent>
    </Sheet>
  );
}

export default function ChauffeursArgent({ chauffeurChoisi, onChoisirChauffeur, peutSaisir = true, nombreAJustifier = 0, onModifie }) {
  const [geste, setGeste] = useState(null);
  const [frais, setFrais] = useState(false);
  const [rafraichir, setRafraichir] = useState(0);
  const [lignesConnues, setLignesConnues] = useState({});
  const apresGeste = () => {
    setGeste(null);
    setFrais(false);
    setRafraichir((n) => n + 1);
    onModifie?.();
  };
  const charger = useCallback(async (params) => {
    const donnees = await soldesChauffeurs(params);
    setLignesConnues((precedent) => ({ ...precedent, ...Object.fromEntries((donnees?.items || []).map((l) => [l.driver_id, l])) }));
    return donnees;
  }, []);
  const colonnes = [
    { cle: 'truck_plate', titre: 'Camion', classe: 'text-muted-foreground' },
    { cle: 'advances', titre: 'Avances', montant: true },
    { cle: 'reimbursed', titre: 'Remboursé', montant: true, masquee: (t) => !Number(t.reimbursed) },
    { cle: 'claims', titre: 'Créances', montant: true, masquee: (t) => !Number(t.claims) },
    { cle: 'justified', titre: 'Frais justifiés', montant: true },
    { cle: 'returned', titre: 'Rendu', montant: true, masquee: (t) => !Number(t.returned) },
    { cle: 'balance', titre: 'Reste chez lui', montant: true, tri: 'reste', classe: 'font-medium' },
    {
      cle: 'last_advance_at',
      titre: 'Dernière avance',
      tri: 'avance',
      rendu: (l) => (l.last_advance_at ? formatDate(l.last_advance_at) : ''),
      total: (t) => (Number(t.holding_count) ? `${pluriel(Number(t.holding_count), 'chauffeur')} à justifier` : ''),
    },
  ];
  const ligne = lignesConnues[chauffeurChoisi];
  return (
    <>
      {nombreAJustifier === null ? <LoadingState shape="table" rows={8} columns={6} label="Chargement des chauffeurs" /> : <TableauSoldes
        cle="chauffeurs"
        testid="tableau-chauffeurs"
        charger={charger}
        colonnes={colonnes}
        identifiant={(l) => l.driver_id}
        nom={{ titre: 'Chauffeur', tri: 'nom', rendu: (l) => nomLisible(l.name, 'Chauffeur') }}
        detailMobile={(l) => [l.truck_plate, l.last_advance_at ? `avance ${formatDate(l.last_advance_at)}` : null].filter(Boolean).join(' · ')}
        montantMobile={{ cle: 'balance', titre: 'Reste' }}
        // Personne n'a d'argent à justifier: tout le monde d'office.
        filtre={{ libelle: 'Argent à justifier', param: 'a_justifier', actifParDefaut: nombreAJustifier > 0 }}
        triParDefaut="reste"
        rechercheLibelle="Rechercher un chauffeur ou une plaque…"
        totalLibelle={(n) => `Total (${pluriel(n, 'chauffeur')})`}
        domaines={['finance', 'paiements', 'depenses', 'trajets', 'chauffeurs']}
        rafraichir={rafraichir}
        vide="Aucun chauffeur n’a d’argent à justifier."
        action={peutSaisir ? (l) => (
          <Button type="button" size="sm" variant="outline" className="h-7 px-2.5 text-xs" onClick={() => setGeste({ geste: 'avance', chauffeur: l.driver_id })}>Avance</Button>
        ) : undefined}
        onOuvrir={(l) => onChoisirChauffeur(l.driver_id)}
      />}
      <PanneauChauffeur
        chauffeurId={chauffeurChoisi || null}
        ligne={ligne}
        peutSaisir={peutSaisir}
        onFermer={() => onChoisirChauffeur('')}
        onGeste={(cle) => setGeste({ geste: cle, chauffeur: chauffeurChoisi })}
        onFrais={() => setFrais(true)}
        rafraichir={rafraichir}
        onModifie={apresGeste}
      />
      <FenetreGeste
        geste={geste?.geste}
        ouvert={Boolean(geste)}
        prefill={{ chauffeur: geste?.chauffeur }}
        onFermer={() => setGeste(null)}
        onTermine={apresGeste}
      />
      <Dialog open={frais} onOpenChange={(o) => !o && setFrais(false)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Frais justifiés</DialogTitle></DialogHeader>
          {frais && <ExpenseForm enFenetre onSaved={() => { toast.success('Frais enregistrés.'); apresGeste(); }} />}
        </DialogContent>
      </Dialog>
    </>
  );
}
